/**
 * Arabic strings, in everyday Palestinian retail wording - the words staff
 * actually say at the till, not literary Arabic and not translated software.
 *
 * "استبدال قطعة" not "إنشاء معاملة إرجاع". "خلص البيع" reads as the natural
 * spoken instruction, and the confirmation uses the same words back.
 *
 * The owner should read this file through with a member of staff before going
 * live; every string here is meant to be argued about and changed.
 */
import type { StringKey } from './en.js';

export const ar: Record<StringKey, string> = {
  // --- general -------------------------------------------------------------
  'app.name': 'نقطة البيع',
  'app.brand': 'AppBay POS',
  'app.tagline': 'الصندوق والمخزون',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.close': 'إغلاق',
  'common.delete': 'حذف',
  'common.edit': 'تعديل',
  'common.add': 'إضافة',
  'common.done': 'تم',
  'common.back': 'رجوع',
  'common.next': 'التالي',
  'common.search': 'بحث',
  'till.left': 'باقي {n}',
  'common.searchPlaceholder': 'ابحث بالاسم أو المقاس أو الباركود',
  'common.loading': 'جاري التحميل',
  'common.none': 'لا شيء',
  'common.all': 'الكل',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.total': 'المجموع',
  'common.subtotal': 'المجموع قبل الخصم',
  'common.qty': 'الكمية',
  'common.price': 'السعر',
  'common.cost': 'التكلفة',
  'common.profit': 'الربح',
  'common.size': 'المقاس',
  'common.colour': 'اللون',
  'common.product': 'الصنف',
  'common.category': 'الفئة',
  'common.brand': 'الماركة',
  'common.supplier': 'المورد',
  'common.barcode': 'الباركود',
  'common.sku': 'الرمز',
  'common.stock': 'المخزون',
  'common.date': 'التاريخ',
  'common.time': 'الوقت',
  'common.name': 'الاسم',
  'common.nameEn': 'الاسم بالإنجليزي',
  'common.nameAr': 'الاسم بالعربي',
  'common.phone': 'رقم الهاتف',
  'common.days': '{n} يوم',
  'common.note': 'ملاحظة',
  'common.notes': 'ملاحظات',
  'common.reason': 'السبب',
  'common.print': 'طباعة',
  'common.export': 'تصدير إلى إكسل',
  'common.exportPdf': 'حفظ PDF',
  'common.confirm': 'متأكد؟',
  'common.required': 'هذا الحقل مطلوب',
  'common.optional': 'اختياري',
  'common.of': 'من',
  'common.items': 'قطع',
  'common.item': 'قطعة',
  'common.customer': 'الزبون',
  'common.cashier': 'الكاشير',
  'common.receipt': 'الفاتورة',
  'common.apply': 'تطبيق',
  'common.clear': 'مسح',
  'common.retry': 'حاول مرة ثانية',
  'common.language': 'اللغة',
  'common.today': 'اليوم',
  'common.yesterday': 'أمس',
  'common.thisWeek': 'هذا الأسبوع',
  'common.thisMonth': 'هذا الشهر',
  'common.lastMonth': 'الشهر الماضي',
  'common.thisYear': 'هذه السنة',
  'common.custom': 'اختر التواريخ',
  'common.from': 'من',
  'common.to': 'إلى',
  'common.filter': 'تصفية',
  'common.noResults': 'ما في نتائج',
  'common.saved': 'تم الحفظ',
  'common.deleted': 'تم الحذف',
  'common.perPage': 'في الصفحة',
  'common.more': 'المزيد',
  'common.menu': 'القائمة',
  'common.signOut': 'تسجيل الخروج',
  'common.discount': 'الخصم',
  'common.vat': 'ضريبة القيمة المضافة',
  'common.change': 'الباقي',
  'common.paid': 'المدفوع',
  'common.due': 'المطلوب',
  'common.amount': 'المبلغ',
  'common.currency': 'العملة',
  'common.rate': 'سعر الصرف',
  'common.percent': 'نسبة',
  'common.fixed': 'مبلغ',
  'common.day': 'اليوم',
  'common.hour': 'الساعة',

  // --- sign in --------------------------------------------------------------
  'login.title': 'تسجيل الدخول',
  'login.username': 'اسم المستخدم',
  'login.password': 'كلمة المرور',
  'login.signIn': 'دخول',
  'login.wrong': 'اسم المستخدم أو كلمة المرور غير صحيحة.',
  'login.tooMany': 'محاولات كثيرة. استنى شوي وجرب مرة ثانية.',
  'login.welcome': 'أهلاً وسهلاً',
  'login.signInHint': 'ادخل باسم المستخدم وكلمة المرور اللي أعطاك ياهم المالك.',
  'login.offlineUnknown': 'ما في إنترنت، وهالشخص ما سجّل دخول على هالجهاز من قبل. اتصل بالإنترنت مرة وحدة وبعدها بيشتغل بدون إنترنت.',
  'login.offlineTooOld': 'ما في إنترنت، ومرّ أكثر من شهر على آخر تسجيل دخول لهالشخص هون. اتصل بالإنترنت مرة وحدة للدخول.',
  'login.offlineSession': 'دخلت بدون إنترنت. كل شي محفوظ على هالجهاز.',
  'login.remember': 'تذكّرني',
  'login.rememberHint': 'ضل مسجّل دخول على هالجهاز لمدة ٣٠ يوم.',
  'login.account': 'رمز الحساب',
  'login.accountHint': 'الرمز اللي انعطى لمحلك. بيضل محفوظ على هالجهاز.',
  'login.unknownAccount': 'ما في محل بهالرمز.',
  'login.accountSuspended': 'حساب هالمحل موقوف. تواصل مع مزوّد النظام.',
  'login.offlineOtherShop': 'ما في إنترنت. الدخول لمحل ثاني بدو اتصال.',
  'login.switchBlocked': 'في {n} بيع على هالجهاز لسا ما انرفعوا. اتصل بالإنترنت وخليهم ينرفعوا قبل ما تدخل لمحل ثاني.',

  // --- navigation -----------------------------------------------------------
  'nav.checkout': 'الصندوق',
  'nav.exchange': 'استبدال',
  'nav.priceCheck': 'فحص السعر',
  'nav.shift': 'ورديتي',
  'nav.held': 'فواتير معلقة',
  'nav.dashboard': 'اليوم',
  'nav.products': 'الأصناف',
  'nav.receive': 'استلام بضاعة',
  'nav.adjust': 'تعديل المخزون',
  'nav.stockCount': 'جرد',
  'nav.labels': 'الملصقات',
  'nav.lists': 'القوائم',
  'nav.sales': 'سجل المبيعات',
  'nav.customers': 'الزبائن',
  'nav.owed': 'مصاري عالزبائن',
  'nav.bills': 'فواتير الموردين',
  'nav.credits': 'رصيد المحل',
  'nav.reports': 'التقارير',
  'nav.expenses': 'المصاريف',
  'nav.settings': 'الإعدادات',
  'nav.users': 'الموظفين',
  'nav.devices': 'الصناديق',
  'nav.audit': 'سجل العمليات',
  'nav.groupSell': 'البيع',
  'nav.groupStock': 'المخزون',
  'nav.groupMoney': 'المال',
  'nav.groupSetup': 'الإعداد',
  'nav.hintDashboard': 'مبيعات اليوم وشو بدّه انتباه',
  'nav.hintCheckout': 'اعمل فاتورة بيع',
  'nav.hintExchange': 'استرجاع غرض أو تبديل مقاس',
  'nav.hintPriceCheck': 'امسح الباركود تشوف السعر والكمية',
  'nav.hintShift': 'افتح الدرج، عدّه، وسكّر اليوم',
  'nav.hintHeld': 'فواتير علّقتها تكمّلها بعدين',
  'nav.hintProducts': 'ضيف أصناف، حدد الأسعار، وشوف المخزون',
  'nav.hintLabels': 'اطبع ملصقات باركود للبضاعة',
  'nav.hintLists': 'الألوان والأصناف والماركات والموردين والمقاسات',
  'nav.hintReceive': 'أدخِل بضاعة جاية من مورّد على المخزون',
  'nav.hintAdjust': 'شطب إشي تالف أو مفقود أو معطى هدية',
  'nav.hintStockCount': 'عِدّ الرفوف وصحّح الأرقام',
  'nav.hintSales': 'كل الفواتير، مع بحث',
  'nav.hintCustomers': 'الزبائن الدائمين وأرقامهم وشو اشتروا',
  'nav.hintOwed': 'مين أخذ على الحساب ولسا ما دفع',
  'nav.hintBills': 'البضاعة اللي إجت ولسا ما تدفعت',
  'nav.hintCredits': 'إشعارات الرصيد اللي أعطيتها وشو باقي فيها',
  'nav.hintExpenses': 'الإيجار والكهرباء وأي مصروف تاني',
  'nav.hintReports': 'المبيعات والربح والضريبة وشو بنباع',
  'nav.hintSettings': 'اسم المحل، الضريبة، العملات، نص الفاتورة',
  'nav.hintUsers': 'مين بقدر يدخل على البرنامج',
  'nav.hintRoles': 'شو مسموح لكل نوع موظف',
  'nav.hintDevices': 'الصناديق والأجهزة اللي بتبيع في المحل',
  'nav.hintAudit': 'مين عمل شو وإيمتى',

  // --- connection -----------------------------------------------------------
  'net.online': 'متصل',
  'net.offline': 'غير متصل',
  'net.syncing': 'جاري الإرسال',
  'net.waiting': '{n} بالانتظار',
  'net.offlineNote': 'فيك تكمل بيع. كل شي محفوظ على الجهاز ورح ينرفع لحاله.',
  'net.storageTitle': 'حفظ البيانات على هالجهاز',
  'net.storageDenied': 'المتصفح ما وعد إنه يحافظ على بيانات هالجهاز. إذا امتلأ القرص، ممكن تضيع مبيعات لسا ما انرفعت. ثبّت التطبيق على الشاشة الرئيسية أو سطح المكتب واطلب مرة ثانية.',
  'net.storageGranted': 'المتصفح وعد إنه يحافظ على بيانات هالجهاز.',
  'net.storageUnsupported': 'هالمتصفح ما بقدر يوعد إنه يحافظ على البيانات. استخدم كروم أو إيدج على هالجهاز.',
  'net.storageRetry': 'اطلب مرة ثانية',
  'net.storageNowKept': 'المتصفح رح يحافظ على بيانات هالجهاز من هلأ.',
  'net.storageStillNo': 'المتصفح لسا بيقول لأ. عادةً تثبيت التطبيق على الشاشة الرئيسية أو سطح المكتب بيغيّر هالشي.',
  'net.storageIos': 'الآيفون والآيباد ما بيعطوا هالوعد - سفاري دايماً بيقول لأ، وما في إعداد بيغيّر هالشي. إذا التطبيق مثبّت على الشاشة الرئيسية بتضل البيانات محفوظة، بس iOS ممكن يمسحها إذا ما انفتح التطبيق لكم أسبوع. يعني منيح لفحص الأسعار والتقارير؛ بس لا تخلي مبيعات مستنية ترتفع على هالجهاز.',
  'net.storageIosShort': 'الآيفون ما بيقدر يوعد يحافظ على البيانات. لا تخلي مبيعات غير مرفوعة على هالجهاز.',
  'net.storageDeniedShort': 'ممكن هالجهاز يضيّع مبيعات مستنية ترتفع. الشرح بالإعدادات ← الصناديق.',
  'net.storageUnsupportedShort': 'هالمتصفح ما بقدر يحافظ على البيانات. استخدم كروم أو إيدج.',
  'net.syncedNow': 'كل شي محدّث',
  'net.staleWarning': 'في فواتير مستنية ترتفع من أكثر من {h} ساعات. افحص الإنترنت.',
  'net.needOnline': 'هذا بدّه اتصال بالإنترنت.',
  'net.stockMayBeOld': 'أرقام المخزون ممكن تكون قديمة والجهاز غير متصل.',
  'net.syncNow': 'أرسل الآن',
  'net.refreshCatalog': 'تحديث قائمة الأصناف',

  // --- checkout -------------------------------------------------------------
  'checkout.title': 'الصندوق',
  'checkout.scanPrompt': 'امسح الباركود للبدء',
  'checkout.scanHint': 'أو ابحث بالاسم من فوق',
  'checkout.empty': 'ما في إشي على هذه الفاتورة',
  'checkout.addItem': 'إضافة صنف',
  'checkout.complete': 'خلص البيع',
  'checkout.completed': 'تم البيع',
  'checkout.newSale': 'فاتورة جديدة',
  'checkout.hold': 'علّق الفاتورة',
  'checkout.held': 'تم تعليق الفاتورة',
  'checkout.cancelSale': 'إلغاء الفاتورة',
  'checkout.cancelConfirm': 'رح ينمسح كل شي على هذه الفاتورة، وما رح تنسجل.',
  'checkout.removeLine': 'شيل',
  'checkout.lineDiscount': 'خصم على هذا السطر',
  'checkout.saleDiscount': 'خصم على كل الفاتورة',
  'checkout.changeSize': 'غيّر المقاس',
  'checkout.unknownBarcode': 'ما في صنف بالباركود {code}.',
  'checkout.searchInstead': 'دوّر عليه',
  'checkout.createProduct': 'أضفه كصنف جديد',
  'checkout.outOfStock': 'بقي {n} بس بالمخزون',
  'checkout.noStock': 'هذا المقاس خلص من المخزون',
  'checkout.priceEach': 'قديش السعر؟',
  'checkout.priceForEach': '{n} قطع، المجموع {total}',
  'catalog.openPrice': 'السعر بينكتب عالصندوق',
  'catalog.openPriceTile': 'السعر عالصندوق',
  'catalog.openPriceHint': 'للأشياء اللي ما إلها سعر ثابت. الكاشير بيكتب السعر وقت البيع، وما بينحسب إلها مخزون.',
  'checkout.sellAnyway': 'بيع على أي حال',
  'checkout.finalSale': 'بيع نهائي - ما بينستبدل',
  'checkout.pay': 'استلام الدفع',
  'checkout.itemsOne': 'قطعة وحدة',
  'checkout.itemsTwo': 'قطعتين',
  'checkout.itemsCount': '{n} قطع',
  'checkout.itemsMany': '{n} قطعة',
  'checkout.shortcuts': 'الاختصارات: F2 دفع، F3 بحث، F4 تعليق، F8 فاتورة جديدة',

  // --- payment --------------------------------------------------------------
  'pay.title': 'استلام الدفع',
  'pay.tab': 'على الحساب',
  'pay.payTabWho': 'على الحساب · حدّد الزبون',
  'pay.payTabNamed': 'على الحساب · {amount} · {name}',
  'pay.tabNote': '{name} بياخدها هلق وبيدفع بعدين. بتنضاف على اللي عليه.',
  'pay.tabNeedsCustomer': 'حط اسم الزبون على الفاتورة أول — دين بدون اسم مش دين.',
  'tab.title': 'مصاري للمحل عند الزبائن',
  'tab.owes': 'عليه',
  'tab.nobody': 'ما في حدا عليه إشي للمحل',
  'tab.nobodyHint': 'الفواتير اللي بتنباع على الحساب بتطلع هون.',
  'tab.total': 'المجموع المستحق',
  'tab.people': '{n} زبون',
  'tab.peopleOne': 'زبون واحد',
  'tab.peopleTwo': 'زبونين',
  'tab.peopleMany': '{n} زبون',
  'tab.takePayment': 'استلم دفعة',
  'tab.takePaymentFrom': 'دفعة من {name}',
  'tab.owesNow': 'عليه هلق',
  'tab.howMuch': 'كم بدفع؟',
  'tab.payAll': 'الكل',
  'tab.paid': 'انحفظت الدفعة',
  'tab.tooMuch': 'هذا أكثر من اللي عليه.',
  'tab.since': 'أقدم دين',
  'tab.lastPaid': 'آخر دفعة',
  'tab.never': 'ولا مرة',
  'tab.statement': 'من شو متكوّن',
  'tab.onAccount': 'انباع على الحساب',
  'tab.paidOff': 'مدفوع',
  'tab.noCustomer': 'هذه الفاتورة بدها زبون',
  'pay.payCash': 'نقداً · {amount}',
  'pay.payCard': 'بطاقة · {amount}',
  'pay.gaveMe': 'أو اضغط على اللي عطاك ياه',
  'pay.otherWay': 'طريقة دفع ثانية',
  'pay.method': 'كيف الزبون رح يدفع؟',
  'pay.cash': 'نقداً',
  'pay.card': 'بطاقة',
  'pay.credit': 'رصيد المحل',
  'pay.other': 'غير ذلك',
  'pay.cardNote': 'أدخل المبلغ على جهاز البنك، وبعدين أكّد هون.',
  'pay.amountDue': 'المطلوب',
  'pay.amountIn': 'المبلغ بـ {currency}',
  'pay.tendered': 'المبلغ المستلم',
  'pay.exact': 'بالضبط',
  'pay.changeDue': 'الباقي للزبون',
  'pay.stillDue': 'باقي عليه',
  'pay.addPayment': 'أضف دفعة ثانية',
  'pay.split': 'تقسيم الدفع',
  'pay.rounding': 'تقريب النقد',
  'pay.roundingNote': 'المجموع النقدي بينقرّب لأقرب ١٠ أغورة.',
  'pay.confirm': 'تأكيد الدفع',
  'pay.creditCode': 'امسح أو اكتب رمز الرصيد',
  'pay.creditBalance': 'الرصيد المتبقي: {amount}',
  'pay.creditNotEnough': 'الرصيد ما بكفّي.',
  'pay.creditNeedsOnline': 'رصيد المحل لازم ينفحص عبر الإنترنت، فما بينستعمل وأنت غير متصل.',
  'pay.gift': 'بطاقة هدية',
  'pay.giftCode': 'امسح البطاقة أو اكتب رقمها',
  'pay.giftBalance': 'رصيد البطاقة: {amount}',
  'pay.giftNotFound': 'ما في بطاقة هدية بهالرقم.',
  'pay.giftOnlyHas': 'بطاقة الهدية فيها {amount}. خذها وحصّل الباقي.',
  'pay.creditOnlyHas': 'الرصيد فيه {amount}. خذه وحصّل الباقي.',
  'pay.creditNotFound': 'ما في رصيد بهالرمز.',
  'pay.giftNotEnough': 'بطاقة الهدية ما بكفّي رصيدها.',
  'pay.giftNeedsOnline': 'بطاقة الهدية لازم تنفحص عبر الإنترنت، فما بتنستعمل وأنت غير متصل.',
  'pay.noChangeFrom': 'ما في فراطة من {method}. خذ بس المبلغ المطلوب.',
  'nav.giftCards': 'بطاقات الهدايا',
  'nav.hintGiftCards': 'بيع بطاقة أو شوف شو باقي فيها',
  'gift.title': 'بطاقات الهدايا',
  'gift.sell': 'بيع بطاقة هدية',
  'gift.amount': 'قديش قيمة البطاقة؟',
  'gift.cardNumber': 'رقم البطاقة',
  'gift.cardNumberHint': 'امسح البطاقة اللي عم تبيعها. اتركها فاضية والصندوق بيطبع قسيمة.',
  'gift.printedVoucher': 'اطبع قسيمة',
  'gift.expiry': 'آخر يوم بتنستعمل فيه',
  'gift.expiryHint': 'اتركها فاضية وما بتنتهي.',
  'gift.takePayment': 'خذ المبلغ',
  'gift.sold': 'انباعت بطاقة {code} بمبلغ {amount}',
  'gift.printCard': 'اطبع البطاقة',
  'gift.empty': 'ما في بطاقات بعد. بِع وحدة وبتبيّن هون.',
  'gift.outstanding': 'بطاقات لسا ما انصرفت',
  'gift.outstandingHint': 'مصاري مقبوضة عن بضاعة لسا ما طلعت من المحل.',
  'gift.liveCards': '{n} بطاقة',
  'gift.remaining': 'الباقي فيها',
  'gift.spent': 'انصرف',
  'gift.soldOn': 'انباعت',
  'gift.expired': 'منتهية',
  'gift.usedUp': 'خلصت',
  'gift.searchPlaceholder': 'رقم البطاقة أو الاسم أو الهاتف',
  'gift.notASale': 'بيع البطاقة مش بيعة: المصاري بالدرج، والضريبة بتنحسب لما تنصرف البطاقة.',
  'gift.voucherTitle': 'بطاقة هدية',
  'gift.voucherNote': 'قدّمها عند الصندوق. بتنصرف على أكثر من زيارة.',
  'pay.payments': 'الدفعات',

  // --- receipt --------------------------------------------------------------
  'receipt.title': 'الفاتورة',
  'receipt.print': 'طباعة الفاتورة',
  'receipt.gift': 'طباعة فاتورة هدية',
  'receipt.reprint': 'طباعة مرة ثانية',
  'receipt.no': 'رقم الفاتورة',
  'receipt.cashier': 'خدمكم',
  'receipt.thanks': 'شكراً لزيارتكم',
  'receipt.exchangePolicy': 'سياسة الاستبدال',
  'receipt.vatNo': 'الرقم الضريبي',
  'receipt.giftNote': 'فاتورة هدية - بدون أسعار',
  'receipt.change': 'الباقي',
  'receipt.paidWith': 'طريقة الدفع',
  'receipt.emailOrPrint': 'أعط الزبون فاتورته',
  'receipt.skip': 'بدون فاتورة',

  // --- shift ----------------------------------------------------------------
  'shift.title': 'ورديتي',
  'shift.open': 'افتح ورديتي',
  'shift.close': 'أغلق ورديتي',
  'shift.opened': 'تم فتح الوردية',
  'shift.closed': 'تم إغلاق الوردية',
  'shift.openingCash': 'النقد بالدرج هلق',
  'shift.closingCash': 'عدّ النقد هلق',
  'shift.countEach': 'عدّ كل عملة واكتب اللي معك فعلاً.',
  'shift.expected': 'المفروض',
  'shift.counted': 'المعدود',
  'shift.difference': 'الفرق',
  'shift.over': 'زيادة',
  'shift.short': 'نقص',
  'shift.exact': 'مضبوط',
  'shift.zReport': 'تقرير نهاية اليوم',
  'shift.printZ': 'اطبع تقرير نهاية اليوم',
  'shift.takings': 'المقبوض خلال الوردية',
  'shift.noShift': 'ما في وردية مفتوحة',
  'shift.startedAt': 'بدأت',
  'shift.salesCount': 'عدد الفواتير',
  'shift.drawerOpen': 'افتح الدرج',
  'shift.drawerReason': 'ليش بدك تفتح الدرج؟',

  // --- exchange -------------------------------------------------------------
  'exchange.title': 'استبدال قطعة',
  'exchange.scanReceipt': 'امسح الفاتورة',
  'exchange.scanHint': 'امسح الباركود على الفاتورة، أو اكتب رقم الفاتورة أو اسم الزبون أو تلفونه.',
  'exchange.findPlaceholder': 'رقم الفاتورة أو الاسم أو التلفون',
  'exchange.searchBy': 'ابحث برقم الفاتورة أو الهاتف أو التاريخ',
  'exchange.noReceipt': 'بدون فاتورة',
  'exchange.noReceiptWarn': 'بدون فاتورة لازم موافقة مدير، والقطعة بتنحسب بأقل سعر انباعت فيه خلال {days} يوم.',
  'exchange.chooseReturning': 'شو راجع؟',
  'exchange.chooseNew': 'شو بدهم ياخدوا بدله؟',
  'exchange.alreadyExchanged': 'تم استبداله سابقاً',
  'exchange.returnedDamaged': 'رجع تالف',
  'exchange.damagedNote': 'ما رح يرجع على الرف.',
  'exchange.paidFor': 'المدفوع',
  'exchange.customerPays': 'الزبون بدفع',
  'exchange.customerGets': 'إله عند المحل',
  'exchange.evenSwap': 'استبدال متساوي، ما في فرق',
  'exchange.asCredit': 'أعطيه رصيد محل',
  'exchange.asCash': 'أرجعله نقد',
  'exchange.complete': 'خلص الاستبدال',
  'exchange.completed': 'تم الاستبدال',
  'exchange.printReceipt': 'اطبع فاتورة الاستبدال',
  'exchange.outsideWindow': 'هذا انشرى من {days} يوم، والمدة المسموحة {window} يوم. لازم موافقة مدير.',
  'exchange.finalSaleBlocked': 'هذه القطعة بيع نهائي. لازم موافقة مدير للاستبدال.',
  'exchange.cashRefundOff': 'إرجاع النقد مقفول. الزبون بياخد رصيد محل.',
  'exchange.nothingSelected': 'اختر شو راجع أول إشي.',
  'exchange.soldOn': 'انباع بتاريخ',
  'exchange.daysAgo': 'من {n} يوم',
  'exchange.giftPrivacy': 'هذه فاتورة هدية. الفاتورة المطبوعة ما رح تبين الأسعار.',
  'exchange.returnOnly': 'إرجاع بدون أخذ إشي',

  // --- manager approval -----------------------------------------------------
  'approval.title': 'بدها موافقة مدير',
  'approval.reasonOverDiscount': 'الخصم أكبر من {limit}% المسموح للكاشير',
  'approval.reasonVoid': 'إلغاء فاتورة مكتملة',
  'approval.reasonOutsideWindow': 'انتهت مدة الاستبدال',
  'approval.reasonNoReceipt': 'ما في فاتورة',
  'approval.reasonFinalSale': 'هذه القطعة بيع نهائي',
  'approval.reasonCashRefund': 'إرجاع نقد',
  'approval.approve': 'موافقة',
  'approval.approved': 'وافق عليها {name}',

  // --- price check ----------------------------------------------------------
  'priceCheck.title': 'فحص السعر',
  'priceCheck.scan': 'امسح أي قطعة',
  'priceCheck.hint': 'السعر وباقي المقاسات رح تبين هون.',
  'priceCheck.otherSizes': 'باقي المقاسات والألوان',
  'priceCheck.inStock': 'متوفر',
  'priceCheck.soldOut': 'خلص',
  'priceCheck.scanAnother': 'امسح قطعة ثانية',

  // --- products -------------------------------------------------------------
  'products.title': 'الأصناف',
  'products.new': 'إضافة صنف',
  'products.itemName': 'شو اسمه؟',
  'products.itemNameHint': 'قميص كتان',
  'products.whatSizes': 'شو المقاسات اللي بيجي فيها؟',
  'products.whatColours': 'شو الألوان؟ (فيك تتخطاها)',
  'products.addVariants': 'إضافة مقاسات أو ألوان',
  'products.addVariantsHint': 'اختار شو كمان بدك هالموديل يجي فيه. اللي موجود أصلاً مقفول، وما بيتغير إشي موجود.',
  'products.sizesToHave': 'المقاسات',
  'products.coloursToHave': 'الألوان',
  'products.willBeAdded': 'هاي اللي رح تتضاف',
  'products.addNSquares': 'أضف {n}',
  'products.nothingNew': 'ما في إشي جديد',
  'products.variantsAdded': 'تمت إضافة {n}',

  'lists.title': 'القوائم',
  'lists.searchPlaceholder': 'دوّر بهاي القائمة',
  'bills.title': 'فواتير الموردين',
  'bills.stillToPay': 'لسا للدفع',
  'bills.paidSoFar': 'المدفوع',
  'bills.left': 'الباقي',
  'bills.partlyPaid': 'مدفوعة جزئياً',
  'bills.openInvoice': 'افتح',
  'bills.noInvoiceNo': 'بضاعة',
  'bills.supplierTitle': 'المورد',
  'bills.billedTotal': 'إجمالي المشتريات',
  'bills.payments': 'الدفعات',
  'bills.noPayments': 'ما تدفع إشي لسا',
  'bills.noPaymentsHint': 'سجّل دفعة لما تعطي المورد مصاري.',
  'bills.whatCameIn': 'شو إجا بهالبضاعة',
  'bills.piecesLines': '{pieces} قطعة بـ {lines} أصناف',
  'bills.recordPayment': 'سجّل دفعة',
  'bills.payingTo': 'بتدفع لـ {supplier}. الفاتورة لسا عليها {amount}.',
  'bills.amountPaid': 'قديش تدفع؟',
  'bills.payTheRest': 'ادفع الباقي ({amount})',
  'bills.tooMuch': 'هذا أكثر من الـ {amount} اللي لسا على الفاتورة.',
  'bills.howPaid': 'كيف تدفعت؟',
  'bills.method_cash': 'كاش',
  'bills.method_bank': 'حوالة بنكية',
  'bills.method_cheque': 'شيك',
  'bills.method_card': 'بطاقة',
  'bills.paidOn': 'يوم ما طلعت المصاري',
  'bills.paidOnHint': 'مش اليوم؟ حط اليوم اللي تدفعت فيه فعلاً.',
  'bills.paymentRecorded': 'تم تسجيل الدفعة',
  'bills.paymentRemoved': 'تم حذف الدفعة',
  'bills.removePayment': 'حذف هالدفعة',
  'bills.removePaymentConfirm': 'حذف دفعة {amount}؟ الفاتورة بترجع مستحقة فيها.',
  'bills.totalOwed': 'مستحق للموردين',
  'bills.invoiceOne': 'فاتورة وحدة',
  'bills.invoiceTwo': 'فاتورتين',
  'bills.invoiceFew': '{n} فواتير',
  'bills.invoiceMany': '{n} فاتورة',
  'bills.paid': 'مدفوعة',
  'bills.unpaid': 'ما تدفعت',
  'bills.noSupplier': 'بدون مورد',
  'bills.allSettled': 'ما عليك إشي',
  'bills.allSettledHint': 'كل البضاعة اللي إجت مدفوعة.',
  'bills.none': 'ما في بضاعة لسا',
  'bills.noneHint': 'الفواتير بتظهر هون بعد ما تستلم بضاعة.',
  'lists.intro': 'الكلمات اللي بتوصف فيها بضاعتك. أي إشي بتضيفه هون بيظهر فوراً بصفحة المنتج. ما في حذف، لأن الفواتير القديمة بتشير عليها - غيّر الاسم بدل ما تحذف.',
  'lists.colours': 'الألوان',
  'lists.categories': 'الأصناف',
  'lists.brands': 'الماركات',
  'lists.suppliers': 'الموردين',
  'lists.sizeScales': 'سلاسل المقاسات',
  'lists.addNew': 'إضافة جديد',
  'lists.empty': 'ما في إشي بهاي القائمة',
  'lists.emptyHint': 'اضغط إضافة جديد لتعمل أول واحد.',
  'lists.saved': 'تم الحفظ',
  'lists.nameEn': 'الاسم بالإنجليزي',
  'lists.nameAr': 'الاسم بالعربي',
  'lists.shade': 'درجة اللون',
  'lists.shadeHint': 'هاي النقطة اللي بتظهر جنب اللون على الكاشير.',
  'lists.theSizes': 'المقاسات، من الأصغر للأكبر',
  'lists.theSizesHint': 'افصل بينهم بفاصلة، مثلاً: S, M, L, XL',
  'lists.scaleEditNote': 'التعديل بيأثر على المنتجات الجديدة. المنتجات الموجودة بتضل بمقاساتها.',
  'products.howMany': 'كم واحد عندك؟',
  'products.howManyHint': 'خليها صفر وأدخِل البضاعة بعدين.',
  'products.howManyEach': 'هالعدد من كل مقاس ولون. بتقدر تعدّل كل واحد بعدين.',
  'products.moreDetails': 'تفاصيل أكثر',
  'products.saveProduct': 'ضيفه',
  'products.createdWithStock': 'انضاف، {n} بالمخزون',
  'products.stockLater': 'الصنف انضاف، بس الكمية ما انحفظت. ضيفها من صفحة الصنف.',
  'products.edit': 'تعديل الصنف',
  'products.empty': 'ما في أصناف بعد',
  'products.firstLoad': 'عم نجيب قائمة الأصناف لهالجهاز. بتاخد لحظة أول مرة.',
  'products.emptyHint': 'أضف أول صنف، وبعدها اطبع ملصقاته.',
  'products.sizeScale': 'سلسلة المقاسات',
  'products.chooseColours': 'الألوان',
  'products.generateGrid': 'النظام رح يعمل قطعة لكل مقاس ولون.',
  'products.gridPreview': 'رح ينعمل {n} قطعة',
  'products.costPrice': 'سعر التكلفة',
  'products.salePrice': 'سعر البيع (شامل الضريبة)',
  'products.season': 'الموسم',
  'products.finalSale': 'بيع نهائي (ما بينستبدل)',
  'products.photo': 'صورة',
  'products.archived': 'مؤرشف',
  'products.archive': 'أرشفة الصنف',
  'products.archiveNote': 'بيضل بالتقارير، وباركوداته ما بتنستعمل مرة ثانية أبداً.',
  'products.variants': 'المقاسات والألوان',
  'products.grid': 'شبكة المقاسات والألوان',
  'products.addSize': 'إضافة مقاس',
  'products.addColour': 'إضافة لون',
  'products.priceOverride': 'سعر مختلف لهذه القطعة',
  'products.lowStockAt': 'نبّهني عند',
  'products.extraBarcode': 'أضف باركود الشركة',
  'products.extraBarcodeHint': 'امسح الباركود المطبوع على القطعة. الاثنين رح يلاقوا نفس المقاس.',
  'products.created': 'تمت إضافة الصنف',
  'products.totalStock': 'إجمالي المخزون',

  // --- stock ----------------------------------------------------------------
  'stock.receive': 'استلام بضاعة',
  'stock.styleSummary': '{sizes} مقاسات · {colors} ألوان',
  'stock.gridLegend': 'اكتب كم إجا. الرقم الرمادي هو اللي عالرف.',
  'stock.newItem': 'صنف جديد',
  'stock.newItemHere': 'صنف جديد بهذه الإرسالية',
  'stock.addToDelivery': 'ضيفه على الإرسالية',
  'stock.newItemAdded': 'انضاف، {n} قطعة على الإرسالية',
  'stock.newItemNotOnList': 'الصنف انعمل، بس ما قدرنا نحطه على القائمة. دوّر عليه بالاسم.',
  'stock.emptyDelivery': 'ما في إشي على هذه الإرسالية',
  'stock.emptyDeliveryHint': 'امسح قطعة، أو دوّر عليها بالاسم، أو اضغط صنف جديد إذا المحل ما جاب منها قبل.',
  'stock.howManyCame': 'كم قطعة إجت؟',
  'stock.howManyCameEach': 'هالعدد من كل مقاس ولون بالإرسالية.',
  'stock.howManyShort': 'الكمية',
  'stock.costHint': 'اللي بتدفعه للمورّد عن القطعة، مش سعر البيع.',
  'stock.deliveryDetails': 'المورّد والفاتورة',
  'stock.piecesCount': '{n} قطع',
  'stock.piecesOne': 'قطعة وحدة',
  'stock.piecesTwo': 'قطعتين',
  'stock.piecesMany': '{n} قطعة',
  'stock.costTotal': 'تكلفة الإرسالية',
  'stock.putIntoStock': 'أدخِلها على المخزون',
  'stock.receiveTitle': 'استلام من مورد',
  'stock.invoiceNo': 'رقم الفاتورة',
  'stock.vatIncluded': 'أسعار الفاتورة شاملة الضريبة',
  'stock.unitCost': 'التكلفة للقطعة',
  'stock.received': 'تم استلام البضاعة',
  'stock.receivedThen': 'بدك تطبع ملصقات للبضاعة اللي استلمتها؟',
  'stock.printLabelsNow': 'اطبع الملصقات',
  'stock.skipLabels': 'مش هلق',
  'stock.markPaid': 'الفاتورة مدفوعة',
  'stock.adjust': 'تعديل المخزون',
  'stock.adjustTitle': 'تغيير رقم مخزون',
  'stock.adjustReason': 'ليش عم يتغير؟',
  'stock.adjusted': 'تم تعديل المخزون',
  'stock.damaged': 'تالف',
  'stock.lost': 'مفقود أو مسروق',
  'stock.countCorrection': 'تصحيح جرد',
  'stock.sample': 'عينة',
  'stock.other': 'أخرى',
  'stock.count': 'جرد',
  'stock.countNew': 'ابدأ جرد',
  'stock.countName': 'شو اسم هذا الجرد؟',
  'stock.countScope': 'جرد كل شي، أو فئة واحدة بس',
  'stock.countScan': 'امسح كل قطعة على الرف',
  'stock.countScanned': 'انمسح {n}',
  'stock.countReview': 'راجع الفروقات',
  'stock.countApply': 'طبّق الفروقات',
  'stock.countApplied': 'تم تطبيق الجرد',
  'stock.countApplyWarn': 'هذا رح يغيّر أرقام المخزون لللي عددته. ما بينرجع.',
  'stock.expected': 'النظام بقول',
  'stock.counted': 'أنت عددت',
  'stock.difference': 'الفرق',
  'stock.noDifferences': 'كل شي مضبوط',
  'stock.movements': 'حركة المخزون',
  'stock.lowStock': 'مخزون قليل',
  'stock.outOfStock': 'خلص',
  'stock.inStock': 'متوفر',

  // --- labels ---------------------------------------------------------------
  'labels.title': 'الملصقات',
  'labels.designer': 'تصميم الملصق',
  'labels.template': 'مقاس الملصق',
  'labels.custom': 'مقاس مخصص',
  'labels.width': 'العرض (مم)',
  'labels.height': 'الارتفاع (مم)',
  'labels.whatToShow': 'شو بدك يطلع على الملصق',
  'labels.showStoreName': 'اسم المحل',
  'labels.showNameEn': 'الاسم بالإنجليزي',
  'labels.showNameAr': 'الاسم بالعربي',
  'labels.showSize': 'المقاس',
  'labels.showColour': 'اللون',
  'labels.showPrice': 'السعر',
  'labels.showBarcodeText': 'رقم الباركود تحته',
  'labels.howMany': 'كم واحد من كل قطعة',
  'labels.queue': 'ملصقات للطباعة',
  'labels.addToQueue': 'أضف لقائمة الطباعة',
  'labels.printAll': 'اطبع {n} ملصق',
  'labels.preview': 'معاينة',
  'labels.empty': 'ما في إشي بقائمة الطباعة',
  'labels.emptyHint': 'اختر الأصناف وكم ملصق لكل واحد.',
  'labels.printHint': 'اضبط مقاس ورق الطابعة على مقاس الملصق، وأطفئ الهوامش.',
  'labels.saveTemplate': 'احفظ هذا المقاس',

  // --- sales history --------------------------------------------------------
  'sales.title': 'سجل المبيعات',
  'sales.searchPlaceholder': 'رقم الفاتورة أو اسم الزبون أو الهاتف',
  'sales.onDay': '{n} فواتير',
  'sales.void': 'إلغاء الفاتورة',
  'sales.voidReason': 'ليش عم تنلغى؟',
  'sales.voided': 'ملغاة',
  'sales.voidedOk': 'تم إلغاء الفاتورة',
  'sales.voidWarn': 'الفاتورة بتضل بالسجلات مكتوب عليها ملغاة، والبضاعة بترجع للمخزون.',
  'sales.viewReceipt': 'شوف الفاتورة',
  'sales.empty': 'ما في مبيعات بهذه الفترة',
  'sales.kindSale': 'بيع',
  'sales.kindExchange': 'استبدال',
  'sales.kindReturn': 'إرجاع',

  // --- customers and credit -------------------------------------------------
  'customers.title': 'الزبائن',
  'customers.new': 'إضافة زبون',
  'customers.empty': 'ما في زبائن بعد',
  'customers.emptyHint': 'الزبائن بس لازمين لرصيد المحل ولإيجاد المشتريات القديمة.',
  'customers.purchases': 'المشتريات السابقة',
  'customers.creditBalance': 'رصيد المحل',
  'customers.tab': 'الحساب المفتوح',
  'customers.payTab': 'سجّل دفعة على الحساب',
  'customers.tabDisabled': 'الحسابات المفتوحة مقفولة من الإعدادات.',
  'credits.title': 'رصيد المحل',
  'credits.new': 'إصدار رصيد',
  'credits.code': 'رمز الرصيد',
  'credits.issued': 'المُصدر',
  'credits.used': 'المستعمل',
  'credits.remaining': 'الباقي',
  'credits.expires': 'ينتهي بتاريخ',
  'credits.expired': 'منتهي',
  'credits.printVoucher': 'اطبع القسيمة',
  'credits.outstanding': 'رصيد محل غير مستعمل',
  'credits.empty': 'ما انصدر أي رصيد',

  // --- reports --------------------------------------------------------------
  'reports.title': 'التقارير',
  'reports.sales': 'المبيعات',
  'reports.profit': 'الأرباح',
  'reports.vat': 'الضريبة',
  'reports.cash': 'النقد والورديات',
  'reports.exchanges': 'الاستبدالات والإرجاعات',
  'reports.inventory': 'قيمة المخزون',
  'reports.stock': 'تقارير المخزون',
  'reports.purchases': 'المشتريات',
  'reports.expenses': 'المصاريف',
  'reports.pl': 'الأرباح والخسائر',
  'reports.grossSales': 'إجمالي المبيعات',
  'reports.discounts': 'الخصومات',
  'reports.returns': 'الإرجاعات والاستبدالات',
  'reports.netSales': 'صافي المبيعات',
  'reports.vatCollected': 'الضريبة المحصّلة',
  'reports.netExVat': 'المبيعات بدون ضريبة',
  'reports.transactions': 'عدد الفواتير',
  'reports.averageSale': 'متوسط الفاتورة',
  'reports.itemsPerSale': 'قطع لكل فاتورة',
  'reports.itemsSold': 'القطع المباعة',
  'reports.cogs': 'تكلفة البضاعة المباعة',
  'reports.grossProfit': 'الربح الإجمالي',
  'reports.margin': 'هامش الربح',
  'reports.rounding': 'تقريب النقد',
  'reports.byDay': 'حسب اليوم',
  'reports.byHour': 'حسب الساعة',
  'reports.byCategory': 'حسب الفئة',
  'reports.byBrand': 'حسب الماركة',
  'reports.byProduct': 'حسب الصنف',
  'reports.bySize': 'حسب المقاس',
  'reports.byColour': 'حسب اللون',
  'reports.byCashier': 'حسب الكاشير',
  'reports.byPayment': 'حسب طريقة الدفع',
  'reports.byCurrency': 'حسب العملة',
  'reports.outputVat': 'ضريبة المبيعات',
  'reports.inputVat': 'ضريبة المشتريات والمصاريف',
  'reports.purchaseVat': 'ضريبة المشتريات',
  'reports.expenseVat': 'ضريبة المصاريف',
  'reports.netPurchasesExVat': 'المشتريات بدون ضريبة',
  'reports.netVat': 'الضريبة المستحقة',
  'reports.vatNote': 'أعط هذا لمحاسبك لتقديم الإقرار الضريبي.',
  'reports.expected': 'المفروض بالدرج',
  'reports.counted': 'المعدود',
  'reports.overShort': 'زيادة أو نقص',
  'reports.atCost': 'بسعر التكلفة',
  'reports.atRetail': 'بسعر البيع',
  'reports.potentialProfit': 'الربح إذا انباع الكل',
  'reports.bestSellers': 'الأكثر مبيعاً',
  'reports.deadStock': 'بضاعة راكدة',
  'reports.deadStockNote': 'ما انباع منها إشي من {days} يوم، ولسا بالمخزون.',
  'reports.lastSold': 'آخر بيعة',
  'reports.never': 'ما انباع أبداً',
  'reports.mostExchanged': 'الأكثر استبدالاً',
  'reports.exchangesBySize': 'الاستبدالات حسب المقاس',
  'reports.sizeHint': 'المقاس اللي بيرجع كثير عادة معناه إنه صغير أو كبير عن اللازم.',
  'reports.creditIssued': 'رصيد صادر',
  'reports.creditUsed': 'رصيد مستعمل',
  'reports.owedToSuppliers': 'مستحق للموردين',
  'reports.owedAllTime': 'كل اللي لسا ما تدفع، مش بس هالتواريخ',
  'reports.owedOfThese': 'غير مدفوع من هدول',
  'reports.netProfit': 'صافي الربح',
  'reports.expensesExVat': 'المصاريف بدون ضريبة',
  'reports.profitNote': 'الربح محسوب على المبيعات والتكاليف بدون ضريبة. الضريبة بتنجمع لدائرة الضريبة ومش مصاري المحل.',
  'reports.noData': 'ما في إشي لهذه التواريخ',
  'reports.vsLastWeek': 'مقارنة بنفس اليوم الأسبوع الماضي',

  // --- dashboard ------------------------------------------------------------
  'dash.title': 'اليوم',
  'dash.todaySales': 'مبيعات اليوم',
  'dash.topToday': 'الأكثر بيعاً اليوم',
  'dash.cashInDrawer': 'النقد بالدرج',
  'dash.lowStockAlert': '{n} صنف مخزونها قليل',
  'dash.openShifts': 'ورديات مفتوحة',
  'dash.noSalesYet': 'ما في مبيعات اليوم لحد هلق',
  'dash.stockValue': 'المخزون بسعر التكلفة',
  'dash.hourly': 'على مدار اليوم',

  // --- expenses -------------------------------------------------------------
  'expenses.title': 'المصاريف',
  'expenses.new': 'تسجيل مصروف',
  'expenses.category': 'على شو كان؟',
  'expenses.rent': 'إيجار',
  'expenses.salaries': 'رواتب',
  'expenses.electricity': 'كهرباء',
  'expenses.water': 'مياه',
  'expenses.cleaning': 'تنظيف',
  'expenses.packaging': 'أكياس وتغليف',
  'expenses.transport': 'مواصلات',
  'expenses.other': 'أخرى',
  'expenses.spentOn': 'التاريخ',
  'expenses.vatOnIt': 'الضريبة ضمن المبلغ',
  'expenses.empty': 'ما في مصاريف مسجلة',

  // --- settings -------------------------------------------------------------
  'settings.title': 'الإعدادات',
  'settings.store': 'المحل',
  'settings.tillTab': 'عند الصندوق',
  'settings.idleSignOut': 'تسجيل الخروج لما ما حدا يستعمل الصندوق',
  'settings.idleSignOutNote': '"أبداً" هو الأنسب لصندوق ورا الكاونتر. ما بيطلع أبداً وقت ما يكون الإنترنت مقطوع.',
  'settings.idleNever': 'أبداً',
  'settings.idleAfter': 'بعد {n} دقيقة',
  'settings.tillNote': 'طفّي الأجزاء اللي محلك ما بيستعملها. ما بينمسح إشي، وكل إشي بيرجع بنفس اللحظة اللي بتشغّله فيها.',
  'settings.useShifts': 'عدّ الدرج عند الفتح والإغلاق',
  'settings.useShiftsNote': 'الكاشير بيفتح وردية بعدّة نقد وبيسكّرها بتقرير آخر اليوم. خلّيها مطفية إذا شخص واحد بيشتغل على الصندوق.',
  'settings.useHold': 'السماح بتعليق فاتورة وإكمالها بعدين',
  'settings.useHoldNote': 'لما حدا يروح يقيس والزبون اللي بعده مستني.',
  'settings.storeName': 'اسم المحل',
  'settings.address': 'العنوان',
  'settings.taxNumber': 'الرقم الضريبي',
  'settings.logo': 'شعار الفواتير',
  'settings.currencies': 'العملات وأسعار الصرف',
  'settings.rateToday': 'سعر اليوم',
  'settings.ratePerUnit': 'كم شيكل للـ {code} الواحد',
  'settings.rateHistory': 'الأسعار السابقة',
  'settings.rateSaved': 'تم حفظ سعر الصرف',
  'settings.rateNote': 'كل فاتورة بتحتفظ بالسعر اللي انستعمل، فالفواتير القديمة ما بتتغير أبداً.',
  'settings.vat': 'ضريبة القيمة المضافة',
  'settings.vatRate': 'نسبة الضريبة (%)',
  'settings.vatNote': 'أسعار الرفوف شاملة الضريبة.',
  'settings.cashStep': 'تقريب النقد إلى (أغورة)',
  'settings.receipt': 'الفواتير',
  'settings.receiptLang': 'لغة الفاتورة',
  'settings.receiptBoth': 'الاثنين',
  'settings.receiptFooter': 'رسالة أسفل الفاتورة',
  'settings.exchanges': 'قواعد الاستبدال',
  'settings.exchangeWindow': 'الاستبدال مسموح لمدة (يوم)',
  'settings.allowCashRefund': 'السماح بإرجاع النقد',
  'settings.allowCashRefundNote': 'مقفول افتراضياً. لما ينفتح، بيضل لازم موافقة مسؤول كل مرة.',
  'settings.noReceiptLookback': 'تقييم الإرجاع بدون فاتورة بأقل سعر خلال (يوم)',
  'settings.enforceFinalSale': 'منع استبدال أصناف البيع النهائي',
  'settings.maxDiscount': 'أقصى خصم للكاشير (%)',
  'settings.appearance': 'المظهر',
  'settings.theme': 'فاتح أو غامق',
  'settings.themeHint': 'التغيير بيبيّن فوراً، وعلى كل الصناديق بعد ما تحفظ.',
  'settings.themeAuto': 'حسب الجهاز',
  'settings.themeAutoHint': 'كل صندوق بيتبع إعداد تلفونه أو كمبيوتره. حالياً هو',
  'settings.themeLight': 'فاتح',
  'settings.themeDark': 'غامق',
  'settings.accent': 'لون المحل',
  'settings.accentHint': 'اختر لون واحد. الأزرار والتمييز والعناوين بيتبعوه، والكتابة اللي عليهم بتنختار لحتى تضل مقروءة.',
  'settings.accentCustom': 'أو لونك الخاص',
  'settings.accentReset': 'رجّع اللون الأصلي',
  'settings.accentPreview': 'معاينة',
  'settings.lowStockDefault': 'تنبيه المخزون القليل عند',
  'settings.deadStockDays': 'اعتبار البضاعة راكدة بعد (يوم)',
  'settings.customerTabs': 'السماح بالحسابات المفتوحة',
  'settings.arabicDigits': 'استعمال الأرقام العربية (٠١٢٣)',
  'settings.unsyncedWarn': 'نبّه إذا انتظرت الفواتير أكثر من (ساعة)',
  'settings.staff': 'الموظفين',
  'settings.addUser': 'إضافة موظف',
  'settings.role': 'شو بيقدر يعمل؟',
  'settings.roleOwner': 'مالك - كل شي',
  'settings.roleManager': 'مدير - مخزون وتقارير وموافقات',
  'settings.roleCashier': 'كاشير - بيع واستبدال',
  'settings.passwordNote': '٨ خانات على الأقل. الكل بيدخل فيها.',
  'settings.maxDiscountUser': 'أقصى خصم لهذا الشخص (%)',
  'settings.devices': 'الصناديق والأجهزة',
  'settings.deviceSeries': 'بادئة الفاتورة',
  'settings.deviceSeriesNote': 'كل صندوق إله بادئة فواتير خاصة، عشان صندوقين بيبيعوا بدون إنترنت ما يطلعوا نفس رقم الفاتورة أبداً.',
  'settings.deviceLastSeen': 'آخر ظهور',
  'settings.deviceThis': 'هذا الجهاز',
  'settings.backups': 'النسخ الاحتياطية',
  'settings.backupNow': 'خذ نسخة احتياطية الآن',
  'settings.backupNote': 'بتنعمل نسخة تلقائياً كل ليلة الساعة ٢. انسخها لمكان تاني كمان.',
  'settings.backupMade': 'تمت النسخة الاحتياطية',
  'settings.rebuildStock': 'إعادة بناء المخزون من السجل',
  'settings.rebuildNote': 'بيعيد حساب كل رقم مخزون من تاريخه. آمن بأي وقت.',
  'settings.rebuilt': 'تمت إعادة بناء المخزون',
  'settings.audit': 'سجل العمليات',
  'settings.paymentMethods': 'طرق الدفع',

  // --- roles and permissions ------------------------------------------------
  'nav.roles': 'الأدوار',
  'roles.title': 'الأدوار والصلاحيات',
  'roles.intro': 'الدور هو اسم مع قائمة بالأشياء اللي مسموح للشخص يعملها. أضف قدّ ما بدك.',
  'roles.new': 'إضافة دور',
  'roles.builtIn': 'جاهز مع النظام',
  'roles.custom': 'من عندك',
  'roles.editPermissions': 'عدّل صلاحياته',
  'roles.permissionCount': '{n} صلاحية',
  'roles.people': '{n} أشخاص',
  'roles.delete': 'حذف هذا الدور',
  'roles.deleteConfirm': 'ما في حدا عنده هذا الدور، فما رح يتغير إشي على أي موظف.',
  'roles.deleteBlocked': '{n} أشخاص لسا عندهم هذا الدور. حوّلهم لدور ثاني أول إشي.',
  'roles.ownerLocked': 'دور المالك دايماً عنده كل الصلاحيات. هذا مقصود: عشان المحل ما يقدر يقفل الباب على حاله.',
  'roles.sensitive': 'حساسة',
  'roles.askOwner': 'اطلب من المالك يضيف هذه الصلاحية لدورك.',
  'roles.forOnePerson': 'لهذا الشخص فقط',
  'roles.overrideHint': 'اضغط على أي صلاحية لتعطيها لهذا الشخص وحده، أو تسحبها منه. باقي الناس بنفس الدور ما بيتأثروا.',
  'roles.fromRole': 'من الدور',
  'roles.added': 'مضافة',
  'roles.removed': 'مسحوبة',
  'roles.role': 'الدور',
  'roles.permissionsTotal': 'بيقدر يعمل {n} إشي',
  'roles.noOverrides': 'نفس الدور بالضبط',
  'roles.overrideSummary': '{added} مضافة، {removed} مسحوبة',
  'roles.lastOwner': 'هذا المالك الوحيد. خلّي حدا ثاني مالك أول إشي.',
  'roles.discountLimit': 'بيقدر يخصم لحد {n}%',
  'roles.discountLimitLabel': 'أقصى خصم لهذا الدور (%)',
  'settings.readOnly': 'بتقدر تشوف هذه الإعدادات بس ما بتقدر تغيّرها.',
  'checkout.resume': 'رجّعها على الصندوق',
  'checkout.someItemsGone': 'في أصناف ما عادت موجودة وانشالت من الفاتورة.',
  'exchange.scanTheItem': 'امسح القطعة الراجعة',
  'exchange.noReceiptOffline': 'ما في اتصال، فانحسبت القطعة بسعر اليوم.',
  'exchange.noReceiptNoHistory': 'هذه القطعة ما انباعت مؤخراً، فانحسبت بسعر اليوم.',
  'checkout.addCustomer': 'اسم الزبون (اختياري)',
  'checkout.noCustomer': 'بدون زبون',
  'approval.wrongLogin': 'اسم المستخدم أو كلمة المرور غير صحيحة، أو هذا الشخص ما بقدر يوافق على هذا.',
  'settings.newPassword': 'كلمة مرور جديدة',
  'settings.passwordKeep': 'اتركها فاضية إذا ما بدك تغيّر كلمة المرور.',
  'settings.inactive': 'غير مفعّل',
  'settings.deviceNextReceipt': 'فواتير هذا الصندوق رح تكون بهذا الشكل {n}',
  'checkout.searchOrScan': 'ابحث أو امسح',
  'checkout.emptyTitle': 'ما في إشي على هذه الفاتورة',
  'checkout.emptyHint': 'اضغط على صنف، أو امسح الباركود.',
  'checkout.takePayment': 'استلام الدفع',
  'checkout.showSale': 'اعرض الفاتورة',
  'checkout.hideSale': 'إخفاء',
  'roles.modules': 'من شو مكوّن هذا النظام',
  'roles.moduleNote': 'كل جزء بينشتغل عليه أو بينستبدل أو بينطفى لحاله.',

  // --- errors ---------------------------------------------------------------
  'error.title': 'صار في مشكلة',
  'error.generic': 'ما زبطت. جرب مرة ثانية.',
  'error.network': 'ما بقدر أوصل للنظام. افحص الإنترنت.',
  'error.notAllowed': 'ما عندك صلاحية لهذا.',
  'error.switchedOff': 'هذا الجزء مطفي',
  'error.switchedOffHint': 'المالك بيقدر يشغّله من الإعدادات، تحت "عند الصندوق".',
  'error.notFound': 'ما لقيناه.',
  'error.sessionEnded': 'انتهت جلستك. سجّل دخول مرة ثانية.',
  'error.tryAgain': 'حاول مرة ثانية',
  'error.goBack': 'ارجع',
  'setup.title': 'جهّز محلك',
  'setup.intro': 'كم تفصيلة، وبيصير الصندوق وشاشة الدخول وكل فاتورة باسم محلك.',
  'setup.later': 'بعدين',
  'setup.step_shop': 'محلك',
  'setup.step_details': 'التواصل والضريبة',
  'setup.step_receipt': 'الفواتير',
  'setup.step_look': 'الألوان',
  'setup.step_password': 'كلمة المرور',
  'setup.step_done': 'خلصنا',
  'setup.shopH': 'شو اسم محلك؟',
  'setup.shopP': 'بيظهر على الصندوق وشاشة الدخول وكل فاتورة. عبّي اللغتين إذا بتقدر.',
  'setup.logo': 'الشعار',
  'setup.logoHint': 'PNG أو JPG. بنصغّره إلك؛ الشعار على خلفية سادة بيطلع أحلى بالطباعة.',
  'setup.logoUpload': 'ارفع الشعار',
  'setup.logoChange': 'غيّر الشعار',
  'setup.logoRemove': 'شيله',
  'setup.logoNotImage': 'هالملف مش صورة.',
  'setup.logoUnreadable': 'ما قدرنا نفتح هالصورة.',
  'setup.logoTooBig': 'الصورة كبيرة كثير حتى بعد التصغير. جرّب ملف أبسط.',
  'setup.previewMenu': 'كيف بيطلع بالقائمة',
  'setup.previewOwner': 'المالك',
  'setup.detailsH': 'بينطبع فوق كل فاتورة',
  'setup.detailsP': 'اترك فاضي أي إشي ما بدك ياه ينطبع: السطر الفاضي ما بيظهر.',
  'setup.vatHint': '16 في فلسطين. 0 إذا ما بتحسب ضريبة.',
  'setup.receiptH': 'فواتيرك',
  'setup.receiptP': 'بأي لغة بتنطبع، والجملة اللي تحت.',
  'setup.previewReceipt': 'معاينة',
  'setup.lookH': 'ألوانك',
  'setup.lookP': 'اختار لون محلك، وشاشات فاتحة أو غامقة. بتقدر تغيّرها أي وقت من الإعدادات.',
  'setup.pwH': 'اختار كلمة مرور إلك',
  'setup.pwP': 'انعطيت كلمة مرور للبداية. غيّرها لوحدة إنت بس بتعرفها.',
  'setup.pwMin': 'على الأقل ٨ أحرف.',
  'setup.pwChange': 'غيّر كلمة المرور',
  'setup.pwKeep': 'خلّي كلمة المرور الحالية',
  'setup.pwDone': 'تغيّرت كلمة المرور.',
  'setup.doneH': '{name} جاهز',
  'setup.doneP': 'كل إشي هون بتقدر تغيّره بعدين من الإعدادات. شو بدك تعمل أول؟',
  'setup.doneProducts': 'ضيف منتجاتي',
  'setup.doneStaff': 'ضيف موظفيني',
  'setup.doneSell': 'ابدأ البيع',
  'login.tillLimit': 'كل صناديق هالمحل مستعملة. المالك بيقدر يفضّي واحد من شاشة الصناديق، أو يطلب زيادة من AppBay.',
  'tills.alertStuck': '{till}: في {n} بيع ما انرفعوا وبدهم مراجعة.',
  'tills.alertWaiting': '{till} عليه {n} بيع مستنيين يترفعوا من {h} ساعة.',
  'tills.see': 'شوف الصناديق',
  'tills.inUse': '{n} من {max} صناديق مستعملة',
  'tills.limitNote': 'كل كمبيوتر بيبيع إله صندوق وتسلسل فواتير خاص. اشطب الصندوق اللي ما عاد بتستعمله عشان يفضى مكانه.',
  'tills.onTill': 'على الصندوق',
  'tills.stuck': '{n} بدهم مراجعة',
  'tills.waiting': '{n} مستنيين',
  'tills.allUp': 'كله انرفع',
  'tills.retire': 'اشطب',
  'tills.restore': 'رجّعه',
  'tills.retiredPill': 'مشطوب',
  'tills.retired': 'انشطب الصندوق وفضي مكانه.',
  'tills.restored': 'رجع الصندوق.',
  'tills.retireTitle': 'شطب {till}؟',
  'tills.retireMsg': 'مبيعاته وأرقام فواتيره بتضل، وبتقدر ترجّعه. الكمبيوتر اللي بيستعمله بدو صندوق فاضي عشان يرجع يبيع.',
  'tills.retireWarn': 'هالصندوق لسا عليه {n} بيع ما وصلوا للسيرفر.',
  'pf.stuck': '{n} عالقة',
  'pf.waiting': '{n} مستنية',
  'pf.stuckLong': '{n} بيع على صناديق هالمحل رفضهم السيرفر وبدهم مراجعة.',
  'pf.waitingLong': '{n} بيع مستنيين يترفعوا من صناديق هالمحل، أقدمهم من {when}.',
  'pf.secTills': 'الصناديق',
  'pf.tillsHint': 'كم كمبيوتر بيقدر هالمحل يبيع منهم بنفس الوقت. كل واحد إله تسلسل فواتير خاص.',
  'pf.tillsAllowed': 'الصناديق المسموحة',
  'pf.tillsOf': '{n} من {max}',
  'pf.ev.tills_changed': 'غيّر عدد الصناديق',
  'sub.endsOn': 'اشتراكك في AppBay بيخلص بتاريخ {date}.',
  'sub.ended': 'اشتراكك في AppBay خلص بتاريخ {date}.',
  'sub.unpaid': 'اشتراكك في AppBay لسا ما اندفع.',
  'sub.contact': 'تواصل مع AppBay للتجديد',
  'sub.waText': 'مرحبا AppBay، بدنا نجدد الاشتراك.',
  'pf.secBilling': 'الاشتراك',
  'pf.bill_not_billed': 'بدون اشتراك',
  'pf.bill_unpaid': 'ما اندفع',
  'pf.bill_active': 'مدفوع',
  'pf.bill_due_soon': 'قرّب يخلص',
  'pf.bill_overdue': 'متأخر',
  'pf.paidUntilLong': 'مدفوع لغاية {date} (باقي {n} يوم).',
  'pf.endedOn': 'خلص بتاريخ {date}، من {n} يوم.',
  'pf.notBilledLong': 'ما في سعر: هالمحل بدون اشتراك.',
  'pf.neverPaid': 'لسا ما في ولا دفعة.',
  'pf.price': 'السعر',
  'pf.notBilled': 'بدون اشتراك',
  'pf.period': 'كل',
  'pf.yearly': 'سنة',
  'pf.monthly': 'شهر',
  'pf.autoPause': 'لما يتأخر',
  'pf.autoPauseHint': 'مطفي: المحل بيضل شغّال وصاحبه بيتذكّر. شغّال: بينوقف بعد أيام السماح.',
  'pf.autoPauseOn': 'أوقف المحل بعد',
  'pf.graceDays': 'يوم',
  'pf.recordPayment': 'سجّل دفعة',
  'pf.amount': 'المبلغ',
  'pf.paidOn': 'تاريخ الدفع',
  'pf.coversUntil': 'بيغطي لغاية',
  'pf.note': 'ملاحظة',
  'pf.notePlaceholder': 'كاش، حوالة، فترة تجربة...',
  'pf.record': 'سجّل',
  'pf.paymentRecorded': 'انسجلت الدفعة.',
  'pf.paymentRemoved': 'انشالت الدفعة.',
  'pf.noPayments': 'لسا ما في دفعات. فترة التجربة المجانية هي دفعة 0 بتغطي الفترة.',
  'pf.remove': 'شيل',
  'pf.removePaymentTitle': 'شيل هالدفعة؟',
  'pf.removePaymentMsg': '{amount} اندفعت بتاريخ {date}. تاريخ "مدفوع لغاية" بينحسب من جديد من الدفعات الباقية.',
  'pf.kpiYearly': 'الاشتراكات، بالسنة',
  'pf.kpiYearlyHint': 'المحلات الشغّالة، الشهري محسوب ×12',
  'pf.kpiLate': 'متأخر أو ما اندفع',
  'pf.dueSoonCount': '{n} بيخلصوا خلال 14 يوم',
  'pf.activePaused': '{a} شغّال · {p} موقوف',
  'pf.colPaidUntil': 'مدفوع لغاية',
  'pf.ev.billing_changed': 'غيّر السعر',
  'pf.ev.payment_recorded': 'سجّل دفعة',
  'pf.ev.payment_removed': 'شال دفعة',
  'pf.ev.auto_paused': 'انوقف تلقائيًا: متأخر',
  'forgot.link': 'نسيت كلمة المرور؟',
  'forgot.title': 'نسيت كلمة المرور',
  'forgot.intro': 'اسم المستخدم بيوصل للشخص اللي بيقدر يعطيك كلمة مرور جديدة: صاحب المحل، أو AppBay لصاحب المحل.',
  'forgot.message': 'ملاحظة (اختياري)',
  'forgot.messageHint': 'مثلاً الرقم اللي بنرجع نتصل عليه.',
  'forgot.send': 'ابعث الطلب',
  'forgot.back': 'رجوع لتسجيل الدخول',
  'forgot.sent': 'انبعث الطلب. لما تنعمل إلك كلمة مرور جديدة بتقدر تدخل فيها.',
  'forgot.ownerHint': 'إنت صاحب المحل؟',
  'forgot.whatsapp': 'ابعث لـ AppBay على واتساب',
  'forgot.waText': 'مرحبا AppBay، نسيت كلمة المرور. رمز الحساب: {code}',
  'forgot.alert': '{n} شخص طلبوا كلمة مرور جديدة.',
  'forgot.alertLink': 'شوف الموظفين',
  'forgot.requestsTitle': 'طلبوا كلمة مرور جديدة',
  'forgot.setNew': 'اعمل كلمة مرور جديدة',
  'forgot.dismiss': 'تجاهل',
  'pf.pwRequests': '{n} طلب كلمة مرور',
  'pf.pwRequest': '{name} طلب كلمة مرور جديدة {when}.',
  'pf.pwRequestOwner': 'صاحب المحل، {name}، طلب كلمة مرور جديدة {when}. إنت بس بتقدر تعملها.',
  'pf.ev.password_request_dismissed': 'تجاهل طلب كلمة مرور',
  'pf.navErrors': 'الأخطاء',
  'pf.errorsSub': 'الأخطاء من كل الصناديق وهاللوحة والسيرفر. نفس الخطأ من صناديق كثيرة بيطلع بسطر واحد.',
  'pf.errorsShowFixed': 'اعرض اللي انحلّت',
  'pf.errorsNone': 'ما في أخطاء',
  'pf.errorsNoneHint': 'لما صندوق أو السيرفر يصير فيه مشكلة، بتظهر هون لحالها.',
  'pf.errServer': 'السيرفر',
  'pf.errTill': 'صندوق',
  'pf.errPanel': 'اللوحة',
  'pf.errTimes': '{n} مرة',
  'pf.errLast': 'آخر مرة {when}',
  'pf.errFirst': 'أول مرة {when}',
  'pf.errFixed': 'انحلّت',
  'pf.errMarkFixed': 'علّمها انحلّت',
  'pf.ev.error_marked_fixed': 'علّم خطأ إنه انحل',
  'import.fromExcel': 'استيراد من إكسل',
  'import.title': 'استيراد أصناف من إكسل',
  'import.intro': 'دخّل كل أصنافك مرة وحدة: سطر لكل مقاس بكل لون. ما في إشي بينحفظ قبل ما تشوف الفحص.',
  'import.step1': 'نزّل النموذج وعبّيه - أو استعمل ملفك: الأعمدة بتنعرف من عناوينها.',
  'import.getTemplate': 'نزّل النموذج',
  'import.step2': 'اختار الملف (xlsx أو csv).',
  'import.choose': 'اختار ملف',
  'import.chooseAnother': 'اختار ملف ثاني',
  'import.step3': 'اقرأ الفحص، وبعدين اكبس استيراد.',
  'import.newProducts': 'أصناف جديدة',
  'import.addedTo': '+ مقاسات جديدة لـ {n} موجودين',
  'import.pieces': 'قطع',
  'import.piecesHint': 'مقاس بلون',
  'import.stock': 'بالمخزون',
  'import.already': 'موجودة بالنظام',
  'import.alreadyHint': 'بتضل زي ما هي',
  'import.errorsTitle': '{n} مشكلة - ما بينستورد إشي قبل ما تنحل',
  'import.errorsHint': 'صلّح هالسطور بملفك، احفظه، واختاره مرة ثانية.',
  'import.row': 'السطر',
  'import.problem': 'المشكلة',
  'import.more': '...و{n} كمان',
  'import.readFrom': 'انقرأ من ورقة "{sheet}"، العناوين بالسطر {row}:',
  'import.ignored': 'أعمدة ما انستعملت:',
  'import.willCreate': 'هدول رح ينضافوا لقوائمك:',
  'import.create.categories': 'أقسام',
  'import.create.colours': 'ألوان',
  'import.create.brands': 'ماركات',
  'import.create.suppliers': 'موردين',
  'import.sizesColours': 'المقاسات والألوان',
  'import.addsTo': 'بينضاف للموجود',
  'import.inSystem': 'موجود بالنظام',
  'import.rowN': 'سطر {n}',
  'import.fixFirst': 'صلّح المشاكل اللي فوق أول.',
  'import.nothingNew': 'كل اللي بهالملف موجود بالنظام.',
  'import.readyHint': 'كله تمام. الاستيراد بيحفظ كل إشي مرة وحدة - أو ولا إشي، إذا صار أي خطأ.',
  'import.go': 'استورد {n} قطعة',
  'import.doneTitle': 'تم الاستيراد',
  'import.doneLine': '{products} صنف جديد، {addedTo} انضافلهم، {pieces} قطعة، {qty} بالمخزون. الصناديق بتوصلها خلال دقائق.',
  'import.seeProducts': 'شوف الأصناف',
  'import.file.old_xls': 'هاد ملف إكسل قديم (.xls). افتحه بإكسل واحفظه "مصنف Excel (.xlsx)"، وبعدين اختاره مرة ثانية.',
  'import.file.csv_not_utf8': 'ملف الـCSV هاد بيخرّب العربي. بإكسل اختار حفظ باسم، وبعدين "CSV UTF-8" - أو احفظه .xlsx.',
  'import.file.unreadable': 'ما قدرنا نقرأ الملف. احفظه .xlsx وجرّب مرة ثانية.',
  'import.file.no_header': 'ما لقينا عناوين أعمدة. أول السطور لازم يكون فيها عناوين زي الاسم، المقاس، اللون، السعر - أو استعمل النموذج.',
  'import.file.no_name_column': 'ما في عمود لاسم الصنف. زيد عمود عنوانه الاسم (أو Name).',
  'import.file.too_many_rows': 'أكثر من 5,000 سطر. قسّم الملف لملفين واستورد كل واحد لحاله.',
  'import.file.too_big': 'الملف أكبر من 5 ميغا. شيل منه الصور أو الأوراق الزيادة، أو قسّمه.',
  'import.file.empty': 'الملف فاضي.',
  'import.err.no_name': 'ما في اسم للصنف.',
  'import.err.no_price': 'ما في سعر لهالصنف - لازم يكون بسطر واحد منه على الأقل.',
  'import.err.bad_price': 'السعر "{value}" مش رقم.',
  'import.err.bad_cost': 'التكلفة "{value}" مش رقم.',
  'import.err.bad_qty': 'الكمية "{value}" مش رقم صحيح.',
  'import.err.bad_barcode': 'الباركود "{value}" ما بينفع (أحرف إنجليزي وأرقام بس، لحد 48).',
  'import.err.dup_piece': 'نفس الصنف والمقاس واللون اللي بالسطر {value}.',
  'import.err.dup_barcode': 'نفس باركود السطر {value}.',
  'import.err.barcode_taken': 'هالباركود موجود على صنف ثاني ({value}).',
  'import.err.archived_code': 'الكود {value} لصنف مؤرشف. استعمل كود ثاني، أو رجّع هالصنف أول.',
  'import.err.ambiguous_name': 'في أكثر من صنف اسمه "{value}". زيد عمود كود الموديل عشان ينلاقى الصح.',
  'import.err.no_stock_permission': 'الملف فيه كميات، ودورك ما بيسمح باستلام بضاعة. فضّي عمود الكمية، أو اسأل صاحب المحل.',
  'import.warn.no_cost': '{n} قطعة بدون تكلفة. تقارير الربح بتحسبها ببلاش لحد ما تزيدها.',
  'import.warn.qty_on_existing': '{n} سطر فيه كمية لقطع موجودة بالنظام. مخزونها ما بيتغير - البضاعة الجديدة بتنستلم من الاستلام.',
  'import.warn.suppliers_off': 'عمود المورد ما انستعمل: الموردين مش مفعّلين لهالمحل.',
  'photo.add': 'زيد صورة',
  'photo.change': 'غيّر',
  'photo.remove': 'شيل',
  'photo.saving': 'عم نحفظ الصورة...',
  'photo.saved': 'انحفظت الصورة. الصناديق بتعرضها خلال دقائق.',
  'photo.removeTitle': 'نشيل الصورة؟',
  'photo.removeMsg': 'الصنف بيضل، بس صورته بتروح.',
  'photo.err.not_image': 'هاد الملف مش صورة.',
  'photo.err.unreadable': 'ما قدرنا نفتح الصورة. جرّب JPG أو PNG.',
  'photo.err.too_big': 'الصورة كبيرة كثير.',
  'photo.err.save': 'ما قدرنا نحفظ الصورة.',
  'nav.promotions': 'العروض',
  'nav.hintPromotions': 'خصومات واشترِ واحصل',
  'promo.onSale': 'العروض',
  'promo.intro': 'عروض بيطبقها الصندوق لحاله، حتى بدون إنترنت، وبتنطبع على الفاتورة. لما يبدأ العرض بتقدر توقفه بس ما بتقدر تغيّره.',
  'promo.new': 'عرض جديد',
  'promo.editTitle': 'تعديل العرض',
  'promo.tabCurrent': 'شغّالة وقادمة',
  'promo.tabOver': 'انتهت',
  'promo.none': 'ما في عروض لسا',
  'promo.noneHint': 'مثلاً: خصم 20% على كل الفساتين هالويكند، أو اشترِ 2 وخذ 1 مجاناً على التيشيرتات.',
  'promo.noneOver': 'ما في عروض منتهية',
  'promo.status.running': 'شغّال',
  'promo.status.soon': 'قادم',
  'promo.status.over': 'انتهى',
  'promo.appliesTo': 'على:',
  'promo.scopeAll': 'كل المحل',
  'promo.nProducts': '{n} صنف',
  'promo.untilStopped': 'لحد ما يتوقف',
  'promo.statSales': '{n} فاتورة',
  'promo.statPieces': '{n} قطعة',
  'promo.statGiven': 'خصم {amount}',
  'promo.stop': 'أوقف',
  'promo.remove': 'احذف',
  'promo.stopTitle': 'نوقف هالعرض هلأ؟',
  'promo.stopMsg': 'الصناديق بتوقف تطبقه خلال دقائق. الفواتير اللي انعملت بتضل بعرضها.',
  'promo.removeTitle': 'نحذف هالعرض؟',
  'promo.removeMsg': 'لسا ما بدأ، يعني ما انباع إشي عليه.',
  'promo.removed': 'انحذف العرض.',
  'promo.stoppedOk': 'توقف العرض.',
  'promo.savedRunning': 'انحفظ. الصناديق بتطبقه خلال دقائق.',
  'promo.savedSoon': 'انحفظ. بيبدأ لحاله بالتاريخ اللي حددته.',
  'promo.nameAr': 'الاسم بالعربي',
  'promo.nameEn': 'الاسم بالإنجليزي',
  'promo.kind': 'النوع',
  'promo.kindPercent': 'خصم بالنسبة',
  'promo.kindBxgy': 'اشترِ واحصل',
  'promo.percentOff': 'نسبة الخصم',
  'promo.buy': 'اشترِ',
  'promo.get': 'وخذ',
  'promo.at': 'بـ',
  'promo.free': 'مجاناً',
  'promo.half': 'نص السعر',
  'promo.scope.all': 'كل المحل',
  'promo.scope.categories': 'أقسام',
  'promo.scope.brands': 'ماركات',
  'promo.scope.products': 'أصناف',
  'promo.nothingToPick': 'ما في إشي هون لسا.',
  'promo.chosen': 'مختار {n}',
  'promo.starts': 'بيبدأ',
  'promo.ends': 'بينتهي',
  'promo.endsHint': 'فاضي: لحد ما توقفه.',
  'promo.rules': 'القطعة بتاخد عرض واحد بس. إذا تداخلت العروض، الصندوق بيختار التركيبة اللي بتوفّر للزبون أكثر. اشترِ واحصل بيخصم أرخص القطع بكل مجموعة.',
  'promo.err.name': 'اعطيه اسم.',
  'promo.err.percent': 'النسبة لازم تكون بين 1 و99 (أو 100 للقطع المجانية).',
  'promo.err.buy': 'كم قطعة يشتري: من 1 لـ 20.',
  'promo.err.get': 'كم قطعة ياخد: من 1 لـ 20.',
  'promo.err.targets': 'اختار واحد على الأقل.',
  'promo.err.dates': 'النهاية لازم تكون بعد البداية.',
  'promo.err.started': 'بدأ خلص، فبس بينوقف.',
  'approval.reasonDrawer': 'فتح الدرج بدون بيع',
  'legal.label': 'الشروط والخصوصية',
  'legal.terms': 'شروط الخدمة',
  'legal.privacy': 'الخصوصية',
  'chart.bestDay': 'أفضل يوم',
  'chart.busiestHour': 'أنشط ساعة',
  'chart.highest': 'الأعلى',
  'audit.what': 'شو صار',
  'audit.details': 'التفاصيل',
  'checkout.heldAddedTo': 'الفاتورة المعلّقة انضافت للفاتورة اللي على الصندوق.',
  'checkout.removed': 'انشال {name}',
  'checkout.undo': 'تراجع',
  'print.title': 'الطباعة',
  'print.intro': 'كل نوع طباعة على أي طابعة، لكل صناديق هالمحل.',
  'print.how': 'كيف نطبع',
  'print.viaWindow': 'نافذة الطباعة',
  'print.viaHelper': 'لحالها، عن طريق مساعد الطباعة',
  'print.windowHow': 'المتصفح بيفتح نافذة الطباعة كل مرة وانت بتكبس طباعة. ما في إشي لازم ينزل. لتطبع بدون نافذة، اختار مساعد الطباعة.',
  'print.checking': 'عم نفحص...',
  'print.running': 'مساعد الطباعة شغّال {version}',
  'print.notRunningShort': 'مساعد الطباعة مش شغّال على هالكمبيوتر',
  'print.oldHelper': 'هالمساعد قديم وما بيطبع - نزّل الجديد',
  'print.checkAgain': 'افحص مرة ثانية',
  'print.installHow': 'نزّل مساعد الطباعة، فك الضغط على كمبيوتر الصندوق واكبس مرتين على "Start with Windows (hidden)" مرة وحدة. بيشتغل بدون شاشة، وبيشتغل مع ويندوز، وبيرجع يشتغل لحاله إذا وقف. وبعدها اكبس افحص مرة ثانية.',
  'print.download': 'نزّل مساعد الطباعة',
  'print.receiptPrinter': 'طابعة الفواتير',
  'print.receiptPrinterHint': 'الفواتير وفواتير الهدايا وإعادة الطباعة وبطاقات الهدايا والدرج.',
  'print.typePrinter': 'ويندوز ما عرض الطابعات. اكتب اسم الطابعة زي ما بيظهر بويندوز بالضبط.',
  'print.paper': 'ورق الفواتير',
  'print.labelPrinter': 'طابعة الملصقات',
  'print.labelPrinterHint': 'ملصقات الأسعار، بمقاس الملصق اللي بشاشة الملصقات.',
  'print.labelsInWindow': 'استعمل نافذة الطباعة',
  'print.notOnThisPc': 'مش على هالكمبيوتر',
  'print.testReceipt': 'اطبع فاتورة تجريبية',
  'print.testLine': 'طباعة تجريبية',
  'print.testSent': 'انبعتت للطابعة.',
  'print.a4Note': 'التقارير وصفحات A4 بتضل بنافذة الطباعة، عشان تنحفظ PDF.',
  'print.auto': 'اطبع الفاتورة بعد كل بيع',
  'print.autoHelper': 'مباشرة على طابعة الفواتير، بدون نافذة.',
  'print.autoWindow': 'نافذة الطباعة بتفتح لحالها وبتضل تكبس طباعة. لتتخطاها، اطبع عن طريق المساعد.',
  'print.drawer': 'في درج نقود',
  'print.drawerNote': 'موصول بطابعة الفواتير. بيحط زر فتح الدرج على الصندوق.',
  'print.drawerOnCash': 'افتحه بعد كل بيع فيه كاش',
  'print.drawerOnCashWindow': 'بدون المساعد، لازم الطابعة نفسها تكون مضبوطة تفتح الدرج لما تطبع (ويندوز: تفضيلات الطباعة، Cash drawer).',
  'print.pin': 'منفذ الدرج',
  'print.pinHint': 'أغلب الأدراج على 2. إذا التجربة ما عملت إشي، جرّب 5، وبعدها الاثنين.',
  'print.pinBoth': 'الاثنين',
  'print.testDrawer': 'افتح الدرج (تجربة)',
  'print.drawerSent': 'انبعت. المفروض الدرج فتح هلأ.',
  'print.advanced': 'متقدم',
  'print.helperUrl': 'عنوان مساعد الطباعة على هالكمبيوتر',
  'print.helperUrlHint': 'بس إذا المساعد شغّال على منفذ ثاني.',
  'print.openDrawer': 'افتح الدرج',
  'print.notRunning': 'مساعد الطباعة مش شغّال على هالكمبيوتر. بمجلده، اكبس مرتين على "Start with Windows (hidden)".',
  'print.failed': 'الطابعة ما طبعت.',
  'print.fellBack': 'انطبعت من نافذة الطباعة بدل: {why}',
  'move.waiting': 'هالصندوق رح ينتقل على {host}. في {n} بيع لسا ما انرفعوا: خليه متصل بالإنترنت وبينتقل لحاله.',
  'error.moduleOff': '{name} مش ضمن نظام هالمحل',
  'error.moduleOffHint': 'تواصل مع مزوّد النظام لإضافتها.',
  'pf.brand': 'مالك النظام',
  'pf.signInTitle': 'ادخل لإدارة المحلات',
  'pf.navShops': 'المحلات',
  'pf.navActivity': 'السجل',
  'pf.navAccount': 'حسابي',
  'pf.signOut': 'خروج',
  'pf.shopsSub': 'كل محل على هالسيرفر، كل واحد بقاعدة بياناته وموظفينه.',
  'pf.newShop': 'محل جديد',
  'pf.search': 'دوّر بالاسم أو الرمز أو الهاتف',
  'pf.kpiShops': 'المحلات',
  'pf.kpiActive': 'شغّالة',
  'pf.kpiPaused': 'موقوفة',
  'pf.kpiTakings': 'المبيعات، آخر ٣٠ يوم',
  'pf.receipts': '{n} فاتورة',
  'pf.colShop': 'المحل',
  'pf.colStatus': 'الحالة',
  'pf.colModules': 'الوحدات',
  'pf.colStaff': 'الموظفين',
  'pf.colSales': 'آخر ٣٠ يوم',
  'pf.colLastSale': 'آخر بيع',
  'pf.colSize': 'البيانات',
  'pf.active': 'شغّال',
  'pf.suspended': 'موقوف',
  'pf.notSetUp': 'لسا ما تجهّز',
  'pf.default': 'الافتراضي',
  'pf.defaultHint': 'الصناديق اللي ما بتكتب رمز حساب بتدخل على هالمحل.',
  'pf.never': 'أبداً',
  'pf.noShops': 'ما في محلات لسا',
  'pf.noShopsHint': 'اعمل أول محل، وبعدين أعطِ صاحبه بيانات الدخول.',
  'pf.noMatch': 'ما في محل بطابق البحث.',
  'pf.statsError': 'ما قدرنا نقرأ أرقام هالمحل.',
  'pf.newSub': 'بيعمل قاعدة بيانات المحل، وأول مستخدم فيه، والوحدات اللي إله.',
  'pf.secShop': 'المحل',
  'pf.shopName': 'اسم المحل',
  'pf.shopNameHint': 'بيظهر على شاشة الدخول والفواتير. المحل بيقدر يغيّره بعدين من الإعدادات.',
  'pf.codeHint': 'اللي بيكتبه الموظفين عند الدخول. أحرف إنجليزية صغيرة وأرقام وشرطات. ما بينغيّر بعدين.',
  'pf.codeFree': 'متاح',
  'pf.codeTaken': 'مستعمل لمحل ثاني',
  'pf.codeBad': 'استعمل من ٢ لـ ٣٢ حرف إنجليزي صغير أو رقم أو شرطة',
  'pf.contactName': 'الشخص المسؤول',
  'pf.contactPhone': 'الهاتف',
  'pf.notes': 'ملاحظات',
  'pf.notesHint': 'إنت بس بتشوفها، مش المحل.',
  'pf.secOwner': 'دخول صاحب المحل',
  'pf.ownerName': 'اسمه',
  'pf.generate': 'ولّد',
  'pf.show': 'أظهر',
  'pf.hide': 'أخفي',
  'pf.passwordHint': 'على الأقل ٨ أحرف. بيقدر يغيّرها بعد ما يدخل.',
  'pf.ownerLang': 'لغته',
  'pf.secModules': 'الوحدات',
  'pf.modulesHint': 'البيع والمنتجات واستلام البضاعة وشاشة اليوم والموظفين والإعدادات موجودة دائماً.',
  'pf.preset': 'ابدأ من',
  'pf.modulesCount': '{n} من {total}',
  'pf.create': 'اعمل المحل',
  'pf.createdTitle': 'انعمل المحل',
  'pf.createdHint': 'أعطِ هدول لصاحب المحل. كلمة المرور ما رح تظهر مرة ثانية.',
  'pf.address': 'العنوان',
  'pf.copy': 'انسخ',
  'pf.copyAll': 'انسخ بيانات الدخول',
  'pf.copied': 'انتسخ',
  'pf.openShop': 'افتح صفحة المحل',
  'pf.createAnother': 'اعمل واحد ثاني',
  'pf.allShops': 'كل المحلات',
  'pf.pause': 'أوقف المحل',
  'pf.resume': 'رجّع المحل',
  'pf.pauseTitle': 'توقيف {name}؟',
  'pf.pauseMsg': 'ما حدا بالمحل بيقدر يدخل أو يستعمل الصندوق لحد ما ترجّعه. ما بينحذف إشي.',
  'pf.pauseDefaultNote': 'هذا المحل الافتراضي، فالصناديق اللي ما بتكتب رمز رح تنرفض كمان.',
  'pf.paused': 'انوقف المحل',
  'pf.resumed': 'رجع المحل',
  'pf.kpiProducts': 'المنتجات',
  'pf.activeCount': '{n} فعّال',
  'pf.kpiTills': 'الصناديق',
  'pf.seen': 'آخر ظهور {when}',
  'pf.since': 'من {date}',
  'pf.modulesSaved': 'انحفظت الوحدات. المحل بيشوف التغيير مع أول حركة.',
  'pf.modulesOffTitle': 'توقيف {list}؟',
  'pf.modulesOffMsg': 'هالشاشات بتختفي عن كل الموظفين. ما بينحذف إشي: لما ترجّعها بيرجع كل إشي.',
  'pf.switchOff': 'وقّف',
  'pf.undo': 'تراجع',
  'pf.colName': 'الاسم',
  'pf.colRole': 'الدور',
  'pf.colLastLogin': 'آخر دخول',
  'pf.inactive': 'غير فعّال',
  'pf.resetPassword': 'كلمة مرور جديدة',
  'pf.resetTitle': 'كلمة مرور جديدة لـ {user}',
  'pf.resetMsg': 'كلمة المرور القديمة بتبطل تشتغل وبيطلع من كل الأجهزة.',
  'pf.resetDone': 'تغيّرت كلمة المرور. أعطيه ياها هلأ، ما رح تظهر مرة ثانية.',
  'pf.setPassword': 'غيّر كلمة المرور',
  'pf.secDetails': 'التواصل والملاحظات',
  'pf.secSignIn': 'كيف بيدخل موظفينه',
  'pf.secActivity': 'السجل',
  'pf.noActivity': 'لسا ما في إشي.',
  'pf.activitySub': 'كل إشي انعمل من هاللوحة، وكل دخول عليها.',
  'pf.system': 'النظام',
  'pf.ev.login': 'دخل',
  'pf.ev.login_failed': 'محاولة دخول فاشلة',
  'pf.ev.password_changed': 'غيّر كلمة مروره',
  'pf.ev.shop_created': 'عمل المحل',
  'pf.ev.modules_changed': 'غيّر الوحدات',
  'pf.ev.shop_suspended': 'أوقف المحل',
  'pf.ev.shop_resumed': 'رجّع المحل',
  'pf.ev.shop_password_reset': 'أعطى حدا كلمة مرور جديدة',
  'pf.ev.shop_details_changed': 'عدّل التواصل والملاحظات',
  'pf.added': 'انضاف: {list}',
  'pf.removed': 'انشال: {list}',
  'pf.signedInAs': 'داخل باسم {user}',
  'pf.lastLogin': 'آخر دخول {when}',
  'pf.changePassword': 'غيّر كلمة المرور',
  'pf.currentPassword': 'كلمة المرور الحالية',
  'pf.newPassword': 'كلمة المرور الجديدة',
  'pf.confirmPassword': 'كلمة المرور الجديدة مرة ثانية',
  'pf.passwordMin': 'على الأقل ١٠ أحرف.',
  'pf.mismatch': 'كلمتين المرور الجديدتين مش متطابقتين.',
  'pf.passwordChanged': 'تغيّرت كلمة المرور. أي جهاز ثاني طلع.',
  'pf.loadFailed': 'ما قدرنا نفتح هالصفحة.',
};
